/**
 * Evidence Modal Component
 *
 * 4-Tab modal for Click-to-Explain functionality.
 * Tab visibility is controlled by user's disclosure level.
 *
 * Uses useEvidence hook for data fetching with mock data fallback.
 *
 * @see WORKPLAN-LENDER-AUDIT-TRACEABILITY-MATRIX.md
 * @see useEvidence.ts
 */

import React, { useState } from 'react';
import { X, Info, Calculator, Database, FileCode, Calendar, ChevronDown, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  EvidenceContract,
  DisclosureLevel,
} from '@/types/evidenceContract';
import { getEvidenceConfig } from '@/config/uiEvidenceMap';
import { getVisibleTabsForMetric, TabKey } from '@/config/disclosurePolicy';
import { useEvidence } from '@/hooks/useEvidence';
import { MODAL_UNAVAILABLE_COPY } from '@/common/scoreDisplay';

// Tab Components
import { EvidenceSummaryTab } from './EvidenceSummaryTab';
import { EvidenceBreakdownTab } from './EvidenceBreakdownTab';
import { EvidenceCalculationTab } from './EvidenceCalculationTab';
import { EvidenceSourcesTab } from './EvidenceSourcesTab';

interface EvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  uiFieldPath: string;
  runId: string;
  /** BUG-UI-092: Member ID for cross-run historical evidence */
  memberId?: string;
  disclosureLevel: DisclosureLevel;
  // Optional: pre-loaded evidence (for testing/mock data)
  initialEvidence?: EvidenceContract;
  /** BUG-UI-061: Income basis data for gross_income_6m comparison */
  incomeBasis?: {
    income_basis_used: 'ATTESTED' | 'DEPOSITED' | 'VERIFIED';
    income_confidence: number;
    income_sources: {
      attested?: number;
      deposited_net?: number;
      verified_gross?: number;
    };
    selection_reason?: string;
  };
  /** BUG-UI-120: Dashboard value for reconciliation with modal display */
  dashboardValue?: {
    value: number;
    formattedValue: string;
    month?: string;
    unit?: 'USD' | 'PERCENT' | 'COUNT';
  };
  /** Option 3: Initial month to display (from card's selected month) */
  initialMonth?: string | null;
}

const TAB_CONFIG: Record<TabKey, { label: string; icon: React.ReactNode }> = {
  summary: { label: 'Summary', icon: <Info className="w-4 h-4" /> },
  breakdown: { label: 'Breakdown', icon: <Database className="w-4 h-4" /> },
  calculation: { label: 'Calculation', icon: <Calculator className="w-4 h-4" /> },
  sources: { label: 'Sources', icon: <Database className="w-4 h-4" /> },
  raw: { label: 'Raw', icon: <FileCode className="w-4 h-4" /> },
};

/**
 * Format month dropdown values based on metric type
 * BUG-UI-012: Month dropdown shows scores as currency ("$37")
 */
function formatMonthValue(value: number, metricKey: string): string {
  // Factors and scores - no currency symbol
  if (metricKey.includes('factor') || metricKey.includes('score') || metricKey.includes('verascore')) {
    return Math.round(value).toString();
  }
  // Ratios and percentages
  if (metricKey.includes('ratio') || metricKey.includes('pct')) {
    return `${value.toFixed(1)}%`;
  }
  // Default to currency for financial metrics
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function EvidenceModal({
  isOpen,
  onClose,
  uiFieldPath,
  runId,
  memberId,
  disclosureLevel,
  initialEvidence,
  incomeBasis,
  dashboardValue,
  initialMonth,
}: EvidenceModalProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('summary');
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);

  // Reset local state when modal opens for a different factor
  // This ensures clean state for each factor (fixes BUG-UI-014)
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('summary');
      setIsMonthDropdownOpen(false);
    }
  }, [isOpen, uiFieldPath]);

  // Use the evidence hook for data fetching (with mock fallback)
  // BUG-UI-092: Pass memberId for cross-run historical evidence
  // BUG-EPIC61-009: Get runMetadata for Trust Panel
  // Option 3: Pass initialMonth so modal opens at selected month
  const {
    evidence,
    isLoading,
    error,
    isMockData,
    refetch,
    availableMonths,
    selectedMonth,
    setSelectedMonth,
    runMetadata,
  } = useEvidence({
    runId,
    memberId,
    uiFieldPath,
    disclosureLevel,
    enabled: isOpen && !initialEvidence,
    initialMonth,
  });

  // Use initialEvidence if provided (for testing)
  const displayEvidence = initialEvidence || evidence;

  // Handle month selection
  const handleMonthSelect = (monthKey: string) => {
    setSelectedMonth(monthKey);
    setIsMonthDropdownOpen(false);
  };

  // Get metric key from UI field path
  const evidenceConfig = getEvidenceConfig(uiFieldPath);
  const metricKey = evidenceConfig?.metric_key || '';

  // BUG-UI-049: Use config label as fallback when evidence is empty
  // BUG-UI-145: Prefer evidenceConfig.label (from uiEvidenceMap, tied to uiFieldPath) over
  // displayEvidence.label (from formatLabel, tied to entity_name). This ensures the modal title
  // matches the table label the user clicked, even when multiple table rows share the same entity_name
  // (e.g., "Gross Income" and "Money In" both use money_in_1m)
  const displayLabel = evidenceConfig?.label || displayEvidence?.label || 'Evidence Details';

  // BUG-UI-050: Context-aware empty state messages
  const emptyStateMessage = getEmptyStateMessage(metricKey, evidenceConfig?.label);

  // Get visible tabs based on metric + disclosure level (uses policy matrix)
  const visibleTabs = getVisibleTabsForMetric(metricKey, disclosureLevel);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - Force light theme for readability */}
      <Card className="relative z-10 w-full max-w-2xl max-h-[80vh] overflow-hidden bg-slate-800 text-white shadow-xl border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-700 bg-slate-800">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {/* BUG-UI-049: Use displayLabel which has config fallback */}
              <CardTitle className="text-lg font-semibold text-white">
                {displayLabel}
              </CardTitle>
              {isMockData && (
                <Badge variant="secondary" className="text-xs bg-amber-500 text-white">
                  Mock Data
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-300 mt-1">
              {displayEvidence?.summary?.one_liner || (isLoading ? 'Loading...' : '')}
            </p>
          </div>

          {/* Month Selector */}
          {availableMonths.length > 0 && (
            <div className="relative mr-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                className="flex items-center gap-2 bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              >
                <Calendar className="w-4 h-4" />
                {availableMonths.find(m => m.month_key === selectedMonth)?.label || 'Select Month'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isMonthDropdownOpen ? 'rotate-180' : ''}`} />
              </Button>

              {isMonthDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 max-h-64 overflow-y-auto bg-slate-700 border border-slate-600 rounded-md shadow-lg z-20">
                  {availableMonths.map((month) => (
                    <button
                      key={month.month_key}
                      onClick={() => handleMonthSelect(month.month_key)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-600 flex items-center justify-between ${
                        month.month_key === selectedMonth ? 'bg-slate-600 text-blue-400' : 'text-white'
                      }`}
                    >
                      <span>{month.label}</span>
                      {/* BUG-UI-012: Format based on metric type, not always currency */}
                      {month.hasData ? (
                        <span className="text-xs text-green-400">
                          {formatMonthValue(month.value, metricKey)}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-slate-700">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="p-0 bg-slate-800">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error && !displayEvidence ? (
            /* BUG-UI-050: Context-aware empty state - not always an error! */
            <div className="p-6 text-center">
              {emptyStateMessage.isPositive ? (
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              ) : (
                <Info className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              )}
              {/* BUG-UI-144: Show credit score value in empty state when available */}
              {metricKey === 'credit_score' && dashboardValue && (
                <div className="mb-4">
                  <div className="text-4xl font-bold text-white">
                    {dashboardValue.formattedValue}
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    {dashboardValue.value >= 750 ? '🟢 Excellent' :
                     dashboardValue.value >= 700 ? '🟢 Good' :
                     dashboardValue.value >= 650 ? '🟡 Fair' :
                     dashboardValue.value >= 600 ? '🟠 Poor' : '🔴 Very Poor'}
                  </div>
                </div>
              )}
              <p className={emptyStateMessage.isPositive ? 'text-green-300' : 'text-slate-300'}>
                {emptyStateMessage.message}
              </p>
              {emptyStateMessage.detail && (
                <p className="text-sm text-slate-400 mt-2">{emptyStateMessage.detail}</p>
              )}
              {/* BUG-UI-103: Show "Last updated" date for credit score */}
              {emptyStateMessage.showLastUpdated && runMetadata?.run_ts && (
                <p className="text-xs text-slate-500 mt-3">
                  Last updated: {new Date(runMetadata.run_ts).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              )}
              {!emptyStateMessage.isPositive && (
                <Button variant="outline" onClick={refetch} className="mt-4 text-white border-slate-600">
                  Retry
                </Button>
              )}
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
              <TabsList className="w-full justify-start border-b border-slate-700 rounded-none px-4 bg-slate-700">
                {visibleTabs.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="gap-2 text-slate-300 data-[state=active]:text-white data-[state=active]:bg-slate-600"
                  >
                    {TAB_CONFIG[tab].icon}
                    {TAB_CONFIG[tab].label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="p-4 overflow-y-auto max-h-[60vh] bg-slate-800 text-white">
                <TabsContent value="summary">
                  {/* BUG-UI-036: Pass selectedMonth for date alignment */}
                  {/* BUG-UI-061: Pass incomeBasis for income comparison display */}
                  {/* BUG-EPIC61-009: Pass runMetadata for Trust Panel */}
                  {/* BUG-UI-120: Pass dashboardValue for reconciliation */}
                  <EvidenceSummaryTab
                    evidence={displayEvidence}
                    selectedMonth={selectedMonth}
                    incomeBasis={incomeBasis}
                    runMetadata={runMetadata}
                    dashboardValue={dashboardValue}
                  />
                </TabsContent>

                {visibleTabs.includes('breakdown') && (
                  <TabsContent value="breakdown">
                    <EvidenceBreakdownTab evidence={displayEvidence} />
                  </TabsContent>
                )}

                {visibleTabs.includes('calculation') && (
                  <TabsContent value="calculation">
                    {/* Pass runMetadata for Analysis Window on VeraScore */}
                    <EvidenceCalculationTab evidence={displayEvidence} runMetadata={runMetadata} />
                  </TabsContent>
                )}

                {visibleTabs.includes('sources') && (
                  <TabsContent value="sources">
                    {/* BUG-UI-061: Pass incomeBasis for self-reported income display */}
                    {/* BUG-UI-097: Pass runMetadata from API for accurate account/txn counts */}
                    {/* IMP-LCU-003: Pass memberId for submission history display */}
                    <EvidenceSourcesTab
                      evidence={displayEvidence}
                      incomeBasis={incomeBasis}
                      apiRunMetadata={runMetadata}
                      memberId={memberId}
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Get context-aware empty state message
 * BUG-UI-050: Some empty states are GOOD (late_payments = 0 is positive!)
 * BUG-UI-103: Added showLastUpdated flag for credit score modal
 */
interface EmptyStateMessage {
  message: string;
  detail?: string;
  isPositive: boolean;
  showLastUpdated?: boolean; // BUG-UI-103: Show "Last updated" date for external data
}

function getEmptyStateMessage(metricKey: string, label?: string): EmptyStateMessage {
  const displayName = label || metricKey;

  // Late payments: zero is GOOD!
  if (metricKey.includes('late_payment')) {
    return {
      message: 'No Late Payments Found',
      detail: 'This is a positive indicator - no late payments were detected in the analyzed period.',
      isPositive: true,
    };
  }

  // Credit score: external data source - BUG-UI-056 FIX + BUG-UI-103 (Last Updated)
  if (metricKey === 'credit_score') {
    return {
      message: 'External Credit Data',
      detail: 'This credit score is provided by Equifax and delivered through our credit data vendor, Credit Reporting Service (CRS). VeraScore does not calculate, change, or influence this score — we display it as received from the credit bureau. The score shown reflects the most recent credit pull available.',
      isPositive: true, // Not negative - just informational
      showLastUpdated: true, // BUG-UI-103: Flag to show last updated date
    };
  }

  // Debt-related: zero could be good
  if (metricKey.includes('debt') && !metricKey.includes('factor')) {
    return {
      message: `No ${displayName} Data`,
      detail: 'No debt obligations were found. This could indicate no liabilities or missing data.',
      isPositive: false,
    };
  }

  // Default message
  // WP-8 Slice 8D: route the default empty state through the lender-safe
  // modal copy so it matches FRIENDLY_COPY / MODAL_UNAVAILABLE_COPY and does
  // not leak internal taxonomy ("No evidence" reads as a system failure).
  return {
    message: MODAL_UNAVAILABLE_COPY.detail_not_available_for_metric,
    detail: `Data for ${displayName} may not be available for this period.`,
    isPositive: false,
  };
}
